import mapboxgl from "mapbox-gl";
import { store } from "../../store";

import { wktToGeoJSON } from "@terraformer/wkt";
import { Feature, GeoJsonProperties, Geometry } from "geojson";
import * as uuid from "uuid";
import { convertBoardingType, convertUTCtoJST } from "../../utils/formatting";
import { IconType } from "../map/icons";
import {
    genericLineLayer,
    genericPointLayer,
    hoverablePointLayer,
} from "../map/layers/generics";
import {
    carLayout,
    hermesArrowLayout,
    trajectoryArrowLayout,
    variableSymbolLayout,
} from "../map/layers/layouts";

// Mapに描画するオブジェクトの定義
// ここに定義された順番で描画されるので
// 一番後ろに描画したいオブジェクト→一番手前に描画したいオブジェクトの順で記載する
export function mapObjects(): Array<MapDrawable> {
    return [
        new NaviShape(),
        new TerminalStatusLoc(),
        new UnladenShape(),
        new UnladenStartLoc(),
        new BreakLoc(),
        new DriverCancelLoc(),
        new UnladenEndLoc(),
    ];
}

interface MapDrawable {
    readonly sourceName: string;
    readonly layers: Array<mapboxgl.Layer>;
    // 表示データからFeatureを作る
    buildFeatures(sourceDataPair: Map<string, any>): void;

    // mapにイベントリスナーを差し込む（load前なのでそれ以外のことはしないほうが良い
    setEventListener(map: mapboxgl.Map): void;
}

// タクシーのアイコンで表示される空車開始位置
class UnladenStartLoc implements MapDrawable {
    readonly sourceName = "unladen_start_loc";
    readonly layers: Array<mapboxgl.Layer> = [
        {
            id: "naviPathsStartIconLyr",
            source: this.sourceName,
            type: "symbol",
            layout: carLayout,
        },
    ];

    buildFeatures(sourceDataPair: Map<string, any>) {
        const loc = store.getState().reserveTripMap.res.unladenStartLoc;
        if (!loc) {
            return;
        }
        sourceDataPair.set(this.sourceName, {
            type: "Feature",
            geometry: wktToGeoJSON(loc),
            properties: { id: uuid.v4() },
        });
    }

    setEventListener(map: mapboxgl.Map): void {}
}

// 丸で表示される空車終了地点。色は終了時の状態で変わる。
// またマウスオーバーで吹き出しが出て詳細情報が確認できる
class UnladenEndLoc implements MapDrawable {
    readonly sourceName = "deliver_start_loc";
    readonly layers = [
        genericPointLayer("deliverStartLocLyr", this.sourceName, 6, "color"),
    ];
    readonly boardingTypeColorMap = new Map([
        ["go", "blue"],
        ["cruising", "green"],
        ["tel", "purple"],
        ["stand", "red"],
        ["cancel_tel", "transparent"],
        ["homing", "transparent"],
        ["error", "transparent"],
        [undefined, "transparent"],
        [null, "transparent"],
    ]);
    buildFeatures(sourceDataPair: Map<string, any>) {
        const prop = store.getState().reserveTripMap;
        const loc = prop.res.unladenEndLoc;
        if (!loc) {
            return;
        }

        const boardingType = prop.res.boardingType;
        const pointColor = this.boardingTypeColorMap.get(boardingType);
        sourceDataPair.set(this.sourceName, {
            type: "Feature",
            geometry: wktToGeoJSON(loc),
            properties: {
                id: uuid.v4(),
                color: pointColor,
                boardingType: boardingType,
                boardingTs: convertUTCtoJST(prop.res.unladenEndTs),
            },
        });
    }

    readonly templateMaker = (feature: any): string => {
        const boardingType = feature.properties.boardingType;
        const boardingTs = feature.properties.boardingTs;
        return `<div><strong>自車両実車地</strong></div>
                <div>実車時刻: ${boardingTs}</div>
                <div>乗車種別: ${convertBoardingType(boardingType)}</div>`;
    };

    setEventListener(map: mapboxgl.Map): void {
        popupByMouseHoverListener(
            map,
            this.layers[0].id,
            this.sourceName,
            this.templateMaker
        );
    }
}

// 黒線で描画される空車時走行経路
class UnladenShape implements MapDrawable {
    readonly sourceName = "unladen_shape";
    readonly layers = [
        genericLineLayer("unladenShapeLyr", this.sourceName, 3, 0.9),
        {
            id: "unladenShapeArrowsLyr",
            source: this.sourceName,
            type: "symbol",
            layout: trajectoryArrowLayout,
        } as mapboxgl.Layer,
    ];
    buildFeatures(sourceDataPair: Map<string, any>) {
        const shape = store.getState().reserveTripMap.res.unladenShape;
        if (!shape) {
            return;
        }
        sourceDataPair.set(this.sourceName, {
            type: "Feature",
            geometry: wktToGeoJSON(shape),
            properties: { id: uuid.v4(), color: "#000000" },
        });
    }

    setEventListener(map: mapboxgl.Map): void {}
}

// 薄緑かピンクで描画されるHermes経路 (乗務員端末で表示されている経路)
class NaviShape implements MapDrawable {
    readonly sourceName = "navi_shape";
    readonly layers = [
        genericLineLayer("naviShapeLyr", this.sourceName, 18, 0.7),
        {
            id: "naviShapeArrowsLyr",
            source: this.sourceName,
            type: "symbol",
            layout: hermesArrowLayout,
        } as mapboxgl.Layer,
    ];
    buildFeatures(sourceDataPair: Map<string, any>) {
        const routes = store.getState().reserveTripMap.res.routes;
        if (!routes) {
            return;
        }
        const features: Array<Feature<Geometry, GeoJsonProperties>> = [];
        for (var route of routes) {
            const shape = route.naviShape;
            if (!shape) {
                continue;
            }
            const waitSeconds = route.waitSeconds ? route.waitSeconds : 0;
            const pointColor = waitSeconds > 0 ? "pink" : "lightgreen";
            const arrowIcon =
                waitSeconds > 0
                    ? IconType.HERMES_WAIT_ARROW
                    : IconType.HERMES_ARROW;
            features.push({
                type: "Feature",
                geometry: wktToGeoJSON(shape),
                properties: {
                    id: uuid.v4(),
                    color: pointColor,
                    icon_image: arrowIcon,
                },
            });
        }
        sourceDataPair.set(this.sourceName, {
            type: "FeatureCollection",
            features: features,
        });
    }
    setEventListener(map: mapboxgl.Map): void {}
}

// (仮)走行経路の下に赤(配車停止)、青(配車可能)で描画される端末ステータス
class TerminalStatusLoc implements MapDrawable {
    readonly sourceName = "terminal_status_loc";
    readonly layers = [
        hoverablePointLayer(
            "terminalStatusLyr",
            this.sourceName,
            10,
            "skyblue"
        ),
        genericPointLayer("terminalStatusLocLyr", this.sourceName, 6, "color"),
    ];
    readonly terminalStateColorMap = new Map([
        ["available", "#8080FF"],
        ["pause", "#FF8080"],
        [undefined, "transparent"],
        [null, "transparent"],
    ]);
    readonly terminalStateLabelMap = new Map([
        ["available", "可"],
        ["pause", "不可"],
        [undefined, ""],
        [null, ""],
    ]);
    buildFeatures(sourceDataPair: Map<string, any>) {
        const statusArray = store.getState().reserveTripMap.res.terminalStatus;
        if (!statusArray) {
            sourceDataPair.set(this.sourceName, {
                type: "FeatureCollection",
                features: [],
            });
            return;
        }

        const features: Array<Feature<Geometry, GeoJsonProperties>> = [];
        for (var status of statusArray) {
            const loc = status.locCoords;
            if (!loc) {
                continue;
            }
            const pointColor = this.terminalStateColorMap.get(status.status);
            const label = this.terminalStateLabelMap.get(status.status);
            features.push({
                type: "Feature",
                geometry: wktToGeoJSON(loc),
                properties: {
                    id: uuid.v4(),
                    color: pointColor,
                    ts: status.ts,
                    appStatus: label,
                },
            });
        }
        sourceDataPair.set(this.sourceName, {
            type: "FeatureCollection",
            features: features,
        });
    }
    readonly templateMaker = (feature: any): string => {
        const ts = feature.properties.ts;
        const appStatus = feature.properties.appStatus;
        return `<div><strong>端末</strong></div>
                <div>時刻: ${convertUTCtoJST(ts)}</div>
                <div>配車: ${appStatus}</div>`;
    };
    setEventListener(map: mapboxgl.Map): void {
        popupByMouseHoverListener(
            map,
            this.layers[0].id,
            this.sourceName,
            this.templateMaker
        );
    }
}

// ５分以上の停車位置が灰色で描画される停車情報
class BreakLoc implements MapDrawable {
    readonly sourceName = "break_loc";
    layers = [
        // FIXME インターフェースが違う
        hoverablePointLayer(
            "breakLocHighlightLyr",
            this.sourceName,
            10,
            "skyblue"
        ),
        genericPointLayer("breakLocLyr", this.sourceName, 6, "color"),
    ];
    buildFeatures(sourceDataPair: Map<string, any>) {
        const breakArray = store.getState().reserveTripMap.res.breaks;
        if (!breakArray) {
            sourceDataPair.set(this.sourceName, {
                type: "FeatureCollection",
                features: [],
            });
            return;
        }

        const features: Array<Feature<Geometry, GeoJsonProperties>> = [];
        for (var status of breakArray) {
            const loc = status.locCoords;
            if (!loc) {
                continue;
            }
            features.push({
                type: "Feature",
                geometry: wktToGeoJSON(loc),
                properties: {
                    id: uuid.v4(),
                    color: "gray",
                    startTs: status.startTs,
                    endTs: status.endTs,
                },
            });
        }
        sourceDataPair.set(this.sourceName, {
            type: "FeatureCollection",
            features: features,
        });
    }

    readonly templateMaker = (feature: any): string => {
        const startTs = feature.properties.startTs;
        const endTs = feature.properties.endTs;
        return `<div><strong>停車位置</strong></div>
                <div>開始時刻: ${convertUTCtoJST(startTs)}</div>
                <div>終了時刻: ${convertUTCtoJST(endTs)}</div>`;
    };
    setEventListener(map: mapboxgl.Map): void {
        popupByMouseHoverListener(
            map,
            this.layers[0].id,
            this.sourceName,
            this.templateMaker
        );
    }
}

// 黒の正方形で描画される
class DriverCancelLoc implements MapDrawable {
    sourceName = "driver_cancel_loc";
    layers = [
        {
            id: "driverCancelLocLyr",
            source: this.sourceName,
            type: "symbol",
            layout: variableSymbolLayout(IconType.SQUARE_BLACK),
        } as mapboxgl.Layer,
    ];
    buildFeatures(sourceDataPair: Map<string, any>) {
        const driverCancelArray = store.getState().reserveTripMap.res
            .driverCancels;
        if (!driverCancelArray) {
            sourceDataPair.set(this.sourceName, {
                type: "FeatureCollection",
                features: [],
            });
            return;
        }

        const features: Array<Feature<Geometry, GeoJsonProperties>> = [];
        for (var driverCancel of driverCancelArray) {
            const loc = driverCancel.locCoords;
            if (!loc) {
                continue;
            }
            features.push({
                type: "Feature",
                geometry: wktToGeoJSON(loc),
                properties: { id: uuid.v4() },
            });
        }
        sourceDataPair.set(this.sourceName, {
            type: "FeatureCollection",
            features: features,
        });
    }
    setEventListener(map: mapboxgl.Map): void {}
}

// このリスナーを使うと
// マウスがそのオブジェクトに入ることで吹き出しがポップアップします。
function popupByMouseHoverListener(
    map: mapboxgl.Map,
    layerName: string,
    sourceName: string,
    templateMaker: (feature: any) => string
) {
    let hoveredItem: string | undefined;
    let popup: mapboxgl.Popup;
    map.on("mouseenter", layerName, (e: any) => {
        map.getCanvas().style.cursor = "pointer";
        hoveredItem = e.features[0].id;

        if (hoveredItem) {
            map.setFeatureState(
                { source: sourceName, id: hoveredItem },
                { hover: false }
            );

            if (e.features && e.features[0] && e.features[0].properties) {
                popup = new mapboxgl.Popup()
                    .setLngLat(e.lngLat)
                    .setHTML(templateMaker(e.features[0]));
                popup.addTo(map);
            }
        }

        map.setFeatureState(
            { source: sourceName, id: hoveredItem },
            { hover: true }
        );
    });

    map.on("mouseleave", layerName, (e: any) => {
        if (hoveredItem) {
            map.setFeatureState(
                { source: sourceName, id: hoveredItem },
                { hover: false }
            );
        }
        map.getCanvas().style.cursor = "initial";
        hoveredItem = undefined;
        popup.remove();
    });
}
